import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Ribbon, Package, Layers, Plus, Pencil, Trash2, Factory, AlertTriangle, Star } from "lucide-react";

// EAN-13 checksum (client-side)
export function eanChecksumValid(ean) {
    if (!ean || !/^\d{13}$/.test(ean)) return false;
    let s = 0;
    for (let i = 0; i < 12; i++) s += parseInt(ean[i]) * (i % 2 ? 3 : 1);
    return (10 - (s % 10)) % 10 === parseInt(ean[12]);
}

// Plurale IT euristico per i colori (nero->neri, verde->verdi, rosa->rose)
export function italianPlural(word) {
    const w = (word || "").trim();
    if (!w) return "";
    const upper = w === w.toUpperCase();
    const startsUpper = w[0] === w[0].toUpperCase();
    const lo = w.toLowerCase();
    let out;
    if (lo.endsWith("io")) out = lo.slice(0, -2) + "i";
    else if (lo.endsWith("o")) out = lo.slice(0, -1) + "i";
    else if (lo.endsWith("e")) out = lo.slice(0, -1) + "i";
    else if (lo.endsWith("a")) out = lo.slice(0, -1) + "e";
    else out = lo + "i";
    if (upper) return out.toUpperCase();
    return startsUpper ? out.charAt(0).toUpperCase() + out.slice(1) : out;
}

function AnagrTab({ resource, title, icon: Ic, fields, extra = [], testPrefix }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const emptyForm = () => Object.fromEntries([["codice", ""], ["descrizione", ""], ...extra.map(f => [f.key, ""]), ["note", ""]]);
    const [form, setForm] = useState(emptyForm());

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get(`/${resource}/`); setItems(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

    const openNew = () => { setEditId(null); setForm(emptyForm()); setOpen(true); };
    const openEdit = (x) => {
        setEditId(x.id);
        const f = { codice: x.codice || "", descrizione: x.descrizione || "", note: x.note || "" };
        for (const e of extra) f[e.key] = x[e.key] ?? "";
        setForm(f);
        setOpen(true);
    };
    const save = async () => {
        try {
            const payload = { ...form };
            for (const e of extra) {
                if (e.type === "int" && payload[e.key] !== "") payload[e.key] = parseInt(payload[e.key]);
                if (payload[e.key] === "") payload[e.key] = null;
            }
            const doSave = async (p) => {
                if (editId) await api.put(`/${resource}/${editId}`, p);
                else await api.post(`/${resource}/`, p);
            };
            try { await doSave(payload); }
            catch (err) {
                const em = formatApiError(err) || "";
                if (em.includes("EAN-13 non valido")) {
                    const ok = window.confirm(`${em}\n\nVuoi salvare comunque?`);
                    if (!ok) throw err;
                    await doSave({ ...payload, force_save: true });
                } else { throw err; }
            }
            toast.success(editId ? "Aggiornato" : "Creato");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (x) => {
        if (!window.confirm(`Eliminare ${x.codice}?`)) return;
        try { await api.delete(`/${resource}/${x.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Ic size={16} className="text-amber-400" />
                    <h3 className="font-display font-bold text-lg">{title}</h3>
                    <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <Button size="sm" onClick={openNew} data-testid={`${testPrefix}-new`}
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={14} /> Nuovo
                </Button>
            </div>

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Descrizione</th>
                                {extra.map(e => <th key={e.key} className="text-right px-5 py-3">{e.label}</th>)}
                                <th className="text-left px-5 py-3">Note</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid={`${testPrefix}-table`}>
                            {loading && <tr><td colSpan={4+extra.length} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={4+extra.length} className="px-5 py-12 text-center text-slate-500 text-sm">Nessun elemento</td></tr>
                            )}
                            {items.map(x => (
                                <tr key={x.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`${testPrefix}-row-${x.codice}`}>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{x.codice}</td>
                                    <td className="px-5 py-3 text-slate-100">{x.descrizione}</td>
                                    {extra.map(e => <td key={e.key} className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x[e.key] ?? "—"}</td>)}
                                    <td className="px-5 py-3 text-slate-500 text-xs truncate max-w-xs">{x.note}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={()=>openEdit(x)} data-testid={`${testPrefix}-edit-${x.codice}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(x)} data-testid={`${testPrefix}-del-${x.codice}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica" : "Nuovo"} · {title}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-slate-400">Codice *</Label>
                            <Input data-testid={`${testPrefix}-input-codice`} value={form.codice}
                                   onChange={e=>setForm({...form, codice: e.target.value.toUpperCase()})}
                                   className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        <div><Label className="text-xs text-slate-400">Descrizione *</Label>
                            <Input data-testid={`${testPrefix}-input-desc`} value={form.descrizione}
                                   onChange={e=>setForm({...form, descrizione: e.target.value})}
                                   className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        {extra.length > 0 && (
                            <div className="grid grid-cols-2 gap-3">
                                {extra.map(e => (
                                    <div key={e.key}><Label className="text-xs text-slate-400">{e.label}</Label>
                                        <Input data-testid={`${testPrefix}-input-${e.key}`}
                                               type={e.type==="int" ? "number" : "text"}
                                               min={e.type==="int" ? "0" : undefined}
                                               value={form[e.key] ?? ""}
                                               onChange={ev=>setForm({...form, [e.key]: ev.target.value})}
                                               placeholder={e.placeholder || ""}
                                               className="bg-slate-950/50 border-slate-800 mt-1" />
                                        {e.hintFn
                                            ? <div className="text-[10px] mt-1">{e.hintFn(form[e.key])}</div>
                                            : e.hint && <div className="text-[10px] text-slate-500 mt-1">{e.hint}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid={`${testPrefix}-input-note`} value={form.note ?? ""}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid={`${testPrefix}-save`} className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AnagrTabCartoni() {
    // Tabella cartoni con matrice "cartoni per pedana" per ciascun tipo pedana.
    // Il valore singolo è stato sostituito da una mappa {tipo_pedana_id: int > 0}.
    const [items, setItems] = useState([]);
    const [tipiPed, setTipiPed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const emptyForm = () => ({ codice: "", descrizione: "",
        lung_int_cm: "", larg_int_cm: "", alt_int_cm: "",
        cartoni_per_pedana_map: {}, note: "" });
    const [form, setForm] = useState(emptyForm());

    const load = async () => {
        setLoading(true);
        try {
            const [c, tp] = await Promise.all([api.get("/cartoni/"), api.get("/tipi-pedana/")]);
            setItems(c.data); setTipiPed(tp.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditId(null); setForm(emptyForm()); setOpen(true); };
    const openEdit = (x) => {
        setEditId(x.id);
        setForm({
            codice: x.codice || "", descrizione: x.descrizione || "",
            lung_int_cm: x.lung_int_cm ?? "", larg_int_cm: x.larg_int_cm ?? "", alt_int_cm: x.alt_int_cm ?? "",
            cartoni_per_pedana_map: { ...(x.cartoni_per_pedana_map || {}) },
            note: x.note || "",
        });
        setOpen(true);
    };
    const setCpp = (tpId, val) => {
        const m = { ...form.cartoni_per_pedana_map };
        const n = parseInt(val);
        if (!n || n <= 0) delete m[tpId]; else m[tpId] = n;
        setForm({ ...form, cartoni_per_pedana_map: m });
    };
    const save = async () => {
        try {
            const payload = {
                codice: form.codice, descrizione: form.descrizione,
                lung_int_cm: form.lung_int_cm === "" ? null : parseInt(form.lung_int_cm),
                larg_int_cm: form.larg_int_cm === "" ? null : parseInt(form.larg_int_cm),
                alt_int_cm: form.alt_int_cm === "" ? null : parseInt(form.alt_int_cm),
                cartoni_per_pedana_map: form.cartoni_per_pedana_map || {},
                note: form.note || null,
            };
            if (editId) await api.put(`/cartoni/${editId}`, payload);
            else await api.post("/cartoni/", payload);
            toast.success(editId ? "Aggiornato" : "Creato");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (x) => {
        if (!window.confirm(`Eliminare ${x.codice}?`)) return;
        try { await api.delete(`/cartoni/${x.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const summarizeMap = (m) => {
        const entries = Object.entries(m || {});
        if (entries.length === 0) return <span className="text-slate-500">—</span>;
        const byId = Object.fromEntries(tipiPed.map(t => [t.id, t.codice]));
        return <span className="font-mono text-xs text-slate-300">{entries.map(([tpid, v]) => `${byId[tpid] || "?"}=${v}`).join(" · ")}</span>;
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Package size={16} className="text-amber-400" />
                    <h3 className="font-display font-bold text-lg">Tipi Cartone</h3>
                    <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <Button size="sm" onClick={openNew} data-testid="cart-new"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={14} /> Nuovo
                </Button>
            </div>

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Descrizione</th>
                                <th className="text-right px-5 py-3">Dim. int. (LxLxH cm)</th>
                                <th className="text-left px-5 py-3">Cartoni per pedana (per tipo)</th>
                                <th className="text-left px-5 py-3">Note</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="cart-table">
                            {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500 text-sm">Nessun cartone</td></tr>
                            )}
                            {items.map(x => (
                                <tr key={x.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`cart-row-${x.codice}`}>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{x.codice}</td>
                                    <td className="px-5 py-3 text-slate-100">{x.descrizione}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-300">
                                        {[x.lung_int_cm, x.larg_int_cm, x.alt_int_cm].map(v => v ?? "—").join(" × ")}
                                    </td>
                                    <td className="px-5 py-3">{summarizeMap(x.cartoni_per_pedana_map)}</td>
                                    <td className="px-5 py-3 text-slate-500 text-xs truncate max-w-xs">{x.note}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={()=>openEdit(x)} data-testid={`cart-edit-${x.codice}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(x)} data-testid={`cart-del-${x.codice}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica" : "Nuovo"} · Tipo Cartone</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Codice *</Label>
                                <Input data-testid="cart-input-codice" value={form.codice}
                                       onChange={e=>setForm({...form, codice: e.target.value.toUpperCase()})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                            <div><Label className="text-xs text-slate-400">Descrizione *</Label>
                                <Input data-testid="cart-input-desc" value={form.descrizione}
                                       onChange={e=>setForm({...form, descrizione: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label className="text-xs text-slate-400">Lung. int. (cm)</Label>
                                <Input type="number" min="0" value={form.lung_int_cm}
                                       onChange={e=>setForm({...form, lung_int_cm: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Larg. int. (cm)</Label>
                                <Input type="number" min="0" value={form.larg_int_cm}
                                       onChange={e=>setForm({...form, larg_int_cm: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Alt. int. (cm)</Label>
                                <Input type="number" min="0" value={form.alt_int_cm}
                                       onChange={e=>setForm({...form, alt_int_cm: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                        </div>

                        {/* Matrice per tipi pedana */}
                        <div>
                            <Label className="text-xs text-slate-400">Cartoni per pedana</Label>
                            <div className="text-[10px] text-slate-500 mt-0.5 mb-2">
                                Compila la capacità di questo cartone per ciascun tipo di pedana. Le caselle vuote significano "non definito". Interi &gt; 0.
                            </div>
                            {tipiPed.length === 0 ? (
                                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-md p-2">
                                    <AlertTriangle size={12} className="inline mr-1" />
                                    Nessun tipo pedana in anagrafica. Aggiungili prima nel tab "Tipi Pedana".
                                </div>
                            ) : (
                                <div className="rounded-md border border-slate-800 divide-y divide-slate-800" data-testid="cart-cpp-matrix">
                                    {tipiPed.map(tp => (
                                        <div key={tp.id} className="flex items-center justify-between px-3 py-2 gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-mono text-amber-300 text-xs">{tp.codice}</span>
                                                <span className="text-sm text-slate-200 truncate">{tp.descrizione}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Input data-testid={`cart-cpp-${tp.codice}`} type="number" min="1"
                                                       placeholder="—"
                                                       value={form.cartoni_per_pedana_map?.[tp.id] ?? ""}
                                                       onChange={e=>setCpp(tp.id, e.target.value)}
                                                       className="bg-slate-950/50 border-slate-800 h-8 w-24 font-mono text-center" />
                                                {form.cartoni_per_pedana_map?.[tp.id] && (
                                                    <button onClick={()=>setCpp(tp.id, "")} data-testid={`cart-cpp-clear-${tp.codice}`}
                                                            className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Cancella riga">
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea value={form.note ?? ""}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid="cart-save" className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AnagrTabCodice({ resource, title, icon: Ic, extra = [], testPrefix }) {
    // like AnagrTab ma col campo "codice_breve" (non "codice") e i campi extra required
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const emptyForm = () => Object.fromEntries([["codice_breve", ""], ["nome", ""], ...extra.map(f => [f.key, ""]), ["note", ""]]);
    const [form, setForm] = useState(emptyForm());
    const [pluralTouched, setPluralTouched] = useState(false);
    const hasPlural = extra.some(e => e.key === "nome_plurale");
    useEffect(() => {
        if (hasPlural && !pluralTouched && form.nome) {
            setForm(f => ({...f, nome_plurale: italianPlural(f.nome)}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.nome, hasPlural, pluralTouched]);
    const isSimple = !extra.some(e => e.required); // clienti/colori usano "nome", misure/confez usano campi tecnici

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get(`/${resource}/`); setItems(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource]);

    const openNew = () => { setEditId(null); setForm(emptyForm()); setOpen(true); };
    const openEdit = (x) => {
        setEditId(x.id);
        const f = { codice_breve: x.codice_breve || "", nome: x.nome || "", note: x.note || "" };
        for (const e of extra) f[e.key] = x[e.key] ?? "";
        setForm(f);
        setPluralTouched(!!x.nome_plurale);
        setOpen(true);
    };
    const save = async () => {
        try {
            const payload = { ...form };
            if (!isSimple && !payload.nome) delete payload.nome;
            for (const e of extra) {
                if (e.type === "int" && payload[e.key] !== "") payload[e.key] = parseInt(payload[e.key]);
                if (payload[e.key] === "") payload[e.key] = null;
            }
            if (editId) await api.put(`/${resource}/${editId}`, payload);
            else await api.post(`/${resource}/`, payload);
            toast.success(editId ? "Aggiornato" : "Creato");
            setOpen(false); setPluralTouched(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (x) => {
        if (!window.confirm(`Eliminare ${x.codice_breve}?`)) return;
        try { await api.delete(`/${resource}/${x.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Ic size={16} className="text-amber-400" />
                    <h3 className="font-display font-bold text-lg">{title}</h3>
                    <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <Button size="sm" onClick={openNew} data-testid={`${testPrefix}-new`}
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={14} /> Nuovo
                </Button>
            </div>
            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">{isSimple ? "Nome" : "Combinazione"}</th>
                                {extra.map(e => <th key={e.key} className="text-right px-5 py-3">{e.label}</th>)}
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid={`${testPrefix}-table`}>
                            {loading && <tr><td colSpan={3+extra.length} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={3+extra.length} className="px-5 py-12 text-center text-slate-500 text-sm">Nessun elemento</td></tr>
                            )}
                            {items.map(x => (
                                <tr key={x.id} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`${testPrefix}-row-${x.codice_breve}`}>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{x.codice_breve}</td>
                                    <td className="px-5 py-3 text-slate-100">{x.nome || `${x.larghezza||""}${x.larghezza ? "×" : ""}${x.lunghezza||""}${x.buste_per_rotolo ? `${x.rotoli_per_cartone}×${x.buste_per_rotolo}` : ""}`}</td>
                                    {extra.map(e => <td key={e.key} className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x[e.key] ?? "—"}</td>)}
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={()=>openEdit(x)} data-testid={`${testPrefix}-edit-${x.codice_breve}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(x)} data-testid={`${testPrefix}-del-${x.codice_breve}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica" : "Nuovo"} · {title}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-slate-400">Codice breve *</Label>
                            <Input data-testid={`${testPrefix}-input-codice`} value={form.codice_breve}
                                   onChange={e=>setForm({...form, codice_breve: e.target.value.toUpperCase()})}
                                   placeholder="SA / N / 16 / 27"
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                        {isSimple && (
                            <div><Label className="text-xs text-slate-400">Nome *</Label>
                                <Input data-testid={`${testPrefix}-input-nome`} value={form.nome}
                                       onChange={e=>setForm({...form, nome: e.target.value})}
                                       placeholder="SASCARO / Nero / …"
                                       className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        )}
                        {extra.length > 0 && (
                            <div className="grid grid-cols-2 gap-3">
                                {extra.map(e => (
                                    <div key={e.key}><Label className="text-xs text-slate-400">{e.label}{e.required && " *"}</Label>
                                        <Input data-testid={`${testPrefix}-input-${e.key}`}
                                               type={e.type==="int" ? "number" : "text"}
                                               min={e.type==="int" ? "0" : undefined}
                                               value={form[e.key] ?? ""}
                                               onChange={ev=>{
                                                   if (e.key === "nome_plurale") setPluralTouched(true);
                                                   setForm({...form, [e.key]: ev.target.value});
                                               }}
                                               placeholder={e.placeholder || ""}
                                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                        {e.hint && <div className="text-[10px] text-slate-500 mt-1">{e.hint}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid={`${testPrefix}-input-note`} value={form.note ?? ""}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid={`${testPrefix}-save`} className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AnagrTabMisure() {
    // Tab dedicata a Misure: come AnagrTabCodice ma con colonna "Preferita" (stellina).
    // Il flag `preferita` è unico per gruppo (stesso `codice_breve`): la descrizione articolo
    // e l'etichetta usano SEMPRE le dimensioni della preferita del gruppo.
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const empty = { codice_breve: "", larghezza: "", lunghezza: "", note: "" };
    const [form, setForm] = useState(empty);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/misure/"); setItems(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
    const openEdit = (x) => {
        setEditId(x.id);
        setForm({ codice_breve: x.codice_breve || "", larghezza: x.larghezza ?? "", lunghezza: x.lunghezza ?? "", note: x.note || "" });
        setOpen(true);
    };
    const save = async () => {
        try {
            const payload = { ...form,
                larghezza: parseInt(form.larghezza) || 0,
                lunghezza: parseInt(form.lunghezza) || 0 };
            if (editId) await api.put(`/misure/${editId}`, payload);
            else await api.post(`/misure/`, payload);
            toast.success(editId ? "Aggiornata" : "Creata");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (x) => {
        if (!window.confirm(`Eliminare misura ${x.codice_breve} ${x.larghezza}×${x.lunghezza}?`)) return;
        try { await api.delete(`/misure/${x.id}`); toast.success("Eliminata"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const setPreferita = async (x) => {
        try {
            await api.post(`/misure/${x.id}/preferita`);
            toast.success(`Preferita del gruppo ${x.codice_breve}: ${x.larghezza}×${x.lunghezza}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Raggruppa per codice_breve per evidenziare gruppi senza preferita
    const byCode = {};
    for (const x of items) {
        (byCode[x.codice_breve] = byCode[x.codice_breve] || []).push(x);
    }
    const orphanCodes = Object.entries(byCode).filter(([, arr]) => arr.length > 0 && !arr.some(a => a.preferita)).map(([c]) => c);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Package size={16} className="text-amber-400" />
                    <h3 className="font-display font-bold text-lg">Misure</h3>
                    <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <Button size="sm" onClick={openNew} data-testid="mis-new"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={14} /> Nuova
                </Button>
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed">
                Le misure con lo stesso <b>Codice</b> formano un <b>gruppo</b> (es. codice <span className="font-mono text-amber-300">03</span> → 53×70, 54×70, 55×70).
                La <b>preferita</b> del gruppo è quella usata nella descrizione articolo e sull'etichetta ZPL.
                Clicca la <Star size={11} className="inline text-amber-300 -mt-0.5" /> per cambiarla: la precedente si spegne da sola.
            </div>
            {orphanCodes.length > 0 && (
                <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
                     data-testid="mis-orphan-warning">
                    <AlertTriangle size={12} className="inline -mt-0.5 mr-1" />
                    Gruppi senza preferita: <span className="font-mono">{orphanCodes.join(", ")}</span>. Clicca la stellina per sceglierla.
                </div>
            )}
            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 w-10">★</th>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Misura</th>
                                <th className="text-right px-5 py-3">Larghezza (cm)</th>
                                <th className="text-right px-5 py-3">Lunghezza (cm)</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="mis-table">
                            {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500 text-sm">Nessuna misura</td></tr>
                            )}
                            {items.map(x => {
                                const groupHasPref = (byCode[x.codice_breve] || []).some(a => a.preferita);
                                return (
                                <tr key={x.id}
                                    className={`border-b border-slate-800/60 hover:bg-slate-800/30 ${x.preferita ? "bg-amber-500/5" : ""}`}
                                    data-testid={`mis-row-${x.codice_breve}-${x.larghezza}x${x.lunghezza}`}>
                                    <td className="px-5 py-3">
                                        <button onClick={()=>setPreferita(x)}
                                                title={x.preferita ? "Preferita del gruppo" : (groupHasPref ? "Rendi preferita (spegne l'altra)" : "Nessuna preferita nel gruppo — clicca per marcarla")}
                                                data-testid={`mis-star-${x.codice_breve}-${x.larghezza}x${x.lunghezza}`}
                                                className={`p-1 rounded transition ${x.preferita ? "text-amber-400" : "text-slate-600 hover:text-amber-300"}`}>
                                            <Star size={16} fill={x.preferita ? "currentColor" : "none"} strokeWidth={x.preferita ? 2 : 1.5} />
                                        </button>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{x.codice_breve}</td>
                                    <td className="px-5 py-3 text-slate-100 font-mono text-xs">{x.larghezza}×{x.lunghezza}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x.larghezza}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x.lunghezza}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={()=>openEdit(x)} data-testid={`mis-edit-${x.codice_breve}-${x.larghezza}x${x.lunghezza}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(x)} data-testid={`mis-del-${x.codice_breve}-${x.larghezza}x${x.lunghezza}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Card>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica" : "Nuova"} · Misura</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-slate-400">Codice gruppo *</Label>
                            <Input data-testid="mis-input-codice" value={form.codice_breve}
                                   onChange={e=>setForm({...form, codice_breve: e.target.value.toUpperCase()})}
                                   placeholder="03 / 05 / SL"
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                            <div className="text-[10px] text-slate-500 mt-1">Più righe con lo stesso codice formano un gruppo. La preferita del gruppo si sceglie con la stellina.</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Larghezza (cm) *</Label>
                                <Input data-testid="mis-input-larghezza" type="number" min="1" value={form.larghezza}
                                       onChange={e=>setForm({...form, larghezza: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Lunghezza (cm) *</Label>
                                <Input data-testid="mis-input-lunghezza" type="number" min="1" value={form.lunghezza}
                                       onChange={e=>setForm({...form, lunghezza: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                        </div>
                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid="mis-input-note" value={form.note ?? ""}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid="mis-save" className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function AnagrTabConfezionamenti() {
    // Tab dedicata a Confezionamenti: come Misure ma con (buste × rotoli). Regole B1/B4:
    // - gruppi multi-riga senza preferita → warning ambra (nessun auto-pick)
    // - gruppi single-row → preferita auto-marcata dal backend al deploy.
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const empty = { codice_breve: "", buste_per_rotolo: "", rotoli_per_cartone: "", note: "" };
    const [form, setForm] = useState(empty);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/confezionamenti/"); setItems(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
    const openEdit = (x) => {
        setEditId(x.id);
        setForm({ codice_breve: x.codice_breve || "",
                  buste_per_rotolo: x.buste_per_rotolo ?? "",
                  rotoli_per_cartone: x.rotoli_per_cartone ?? "",
                  note: x.note || "" });
        setOpen(true);
    };
    const save = async () => {
        try {
            const payload = { ...form,
                buste_per_rotolo: parseInt(form.buste_per_rotolo) || 0,
                rotoli_per_cartone: parseInt(form.rotoli_per_cartone) || 0 };
            if (editId) await api.put(`/confezionamenti/${editId}`, payload);
            else await api.post(`/confezionamenti/`, payload);
            toast.success(editId ? "Aggiornato" : "Creato");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (x) => {
        if (!window.confirm(`Eliminare confezionamento ${x.codice_breve} ${x.rotoli_per_cartone}×${x.buste_per_rotolo}?`)) return;
        try { await api.delete(`/confezionamenti/${x.id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const setPreferita = async (x) => {
        try {
            await api.post(`/confezionamenti/${x.id}/preferita`);
            toast.success(`Preferita del gruppo ${x.codice_breve}: ${x.rotoli_per_cartone}×${x.buste_per_rotolo}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const byCode = {};
    for (const x of items) {
        (byCode[x.codice_breve] = byCode[x.codice_breve] || []).push(x);
    }
    const orphanCodes = Object.entries(byCode).filter(([, arr]) => arr.length > 1 && !arr.some(a => a.preferita)).map(([c]) => c);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Layers size={16} className="text-amber-400" />
                    <h3 className="font-display font-bold text-lg">Confezionamenti</h3>
                    <span className="text-xs text-slate-500">({items.length})</span>
                </div>
                <Button size="sm" onClick={openNew} data-testid="cfz-new"
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                    <Plus size={14} /> Nuovo
                </Button>
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed">
                Confezionamenti con lo stesso <b>Codice</b> formano un <b>gruppo</b> (es. codice <span className="font-mono text-amber-300">C1</span> → 15×4, 20×4).
                La <b>preferita</b> del gruppo pilota l'import quando la fascetta indica solo le buste per rotolo.
                Clicca la <Star size={11} className="inline text-amber-300 -mt-0.5" /> per cambiarla.
            </div>
            {orphanCodes.length > 0 && (
                <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
                     data-testid="cfz-orphan-warning">
                    <AlertTriangle size={12} className="inline -mt-0.5 mr-1" />
                    Gruppi multi-riga senza preferita: <span className="font-mono">{orphanCodes.join(", ")}</span>. Clicca la stellina per sceglierla.
                </div>
            )}
            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3 w-10">★</th>
                                <th className="text-left px-5 py-3">Codice</th>
                                <th className="text-left px-5 py-3">Buste × Rotoli</th>
                                <th className="text-right px-5 py-3">Buste/Rotolo</th>
                                <th className="text-right px-5 py-3">Rotoli/Cartone</th>
                                <th className="text-right px-5 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody data-testid="cfz-table">
                            {loading && <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500 text-sm">Nessun confezionamento</td></tr>
                            )}
                            {items.map(x => (
                                <tr key={x.id}
                                    className={`border-b border-slate-800/60 hover:bg-slate-800/30 ${x.preferita ? "bg-amber-500/5" : ""}`}
                                    data-testid={`cfz-row-${x.codice_breve}-${x.buste_per_rotolo}x${x.rotoli_per_cartone}`}>
                                    <td className="px-5 py-3">
                                        <button onClick={()=>setPreferita(x)}
                                                title={x.preferita ? "Preferita del gruppo" : "Rendi preferita del gruppo (spegne l'altra)"}
                                                data-testid={`cfz-star-${x.codice_breve}-${x.buste_per_rotolo}x${x.rotoli_per_cartone}`}
                                                className={`p-1 rounded transition ${x.preferita ? "text-amber-400" : "text-slate-600 hover:text-amber-300"}`}>
                                            <Star size={16} fill={x.preferita ? "currentColor" : "none"} strokeWidth={x.preferita ? 2 : 1.5} />
                                        </button>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-amber-300 text-xs">{x.codice_breve}</td>
                                    <td className="px-5 py-3 text-slate-100 font-mono text-xs">{x.rotoli_per_cartone}×{x.buste_per_rotolo}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x.buste_per_rotolo}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-slate-300">{x.rotoli_per_cartone}</td>
                                    <td className="px-5 py-3 text-right">
                                        <button onClick={()=>openEdit(x)} data-testid={`cfz-edit-${x.codice_breve}-${x.buste_per_rotolo}x${x.rotoli_per_cartone}`}
                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                        <button onClick={()=>del(x)} data-testid={`cfz-del-${x.codice_breve}-${x.buste_per_rotolo}x${x.rotoli_per_cartone}`}
                                                className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica" : "Nuovo"} · Confezionamento</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-slate-400">Codice gruppo *</Label>
                            <Input data-testid="cfz-input-codice" value={form.codice_breve}
                                   onChange={e=>setForm({...form, codice_breve: e.target.value.toUpperCase()})}
                                   placeholder="C1 / R25"
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs text-slate-400">Buste/Rotolo *</Label>
                                <Input data-testid="cfz-input-buste" type="number" min="1" value={form.buste_per_rotolo}
                                       onChange={e=>setForm({...form, buste_per_rotolo: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                            <div><Label className="text-xs text-slate-400">Rotoli/Cartone *</Label>
                                <Input data-testid="cfz-input-rotoli" type="number" min="1" value={form.rotoli_per_cartone}
                                       onChange={e=>setForm({...form, rotoli_per_cartone: e.target.value})}
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono" /></div>
                        </div>
                        <div><Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid="cfz-input-note" value={form.note ?? ""}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={save} data-testid="cfz-save" className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}



import { useAuth } from "@/context/AuthContext";

export default function Anagrafiche({ embedded = false }) {
    const { user, can } = useAuth();
    const isAdmin = user?.role === "admin";
    const seeFasc = isAdmin || can("anagr.fascette");
    const seeCart = isAdmin || can("anagr.cartoni");
    const seeTped = isAdmin || can("anagr.tipi_pedana");
    const seeImp = isAdmin || can("anagr.impianti");
    const seeCli = isAdmin || can("anagr.clienti");
    const seeCol = isAdmin || can("anagr.colori");
    const seeMis = isAdmin || can("anagr.misure");
    const seeCfz = isAdmin || can("anagr.confezionamenti");
    const firstAvail = seeFasc ? "fascette" : seeCart ? "cartoni" : seeTped ? "pedane" : seeImp ? "impianti"
        : seeCli ? "clienti" : seeCol ? "colori" : seeMis ? "misure" : seeCfz ? "confezionamenti" : "fascette";
    return (
        <div className="space-y-6">
            {!embedded && (
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-500/80">CONFIGURAZIONE</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Anagrafiche di base</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                        Fascette (carta rotolo), tipi di cartone, tipi di pedana.
                        {isAdmin && " Sezione Admin: clienti, colori, misure, confezionamenti (di solito auto-generati)."}
                    </p>
                </div>
            )}
            <Tabs defaultValue={firstAvail}>
                <TabsList data-testid="tabs-anagrafiche" className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
                    {seeFasc && <TabsTrigger value="fascette" data-testid="tab-fascette">Fascette</TabsTrigger>}
                    {seeCart && <TabsTrigger value="cartoni" data-testid="tab-cartoni">Cartoni</TabsTrigger>}
                    {seeTped && <TabsTrigger value="pedane" data-testid="tab-tipi-pedana">Tipi Pedana</TabsTrigger>}
                    {seeImp && <TabsTrigger value="impianti" data-testid="tab-impianti">Impianti</TabsTrigger>}
                    {seeCli && <TabsTrigger value="clienti" data-testid="tab-clienti">Clienti</TabsTrigger>}
                    {seeCol && <TabsTrigger value="colori" data-testid="tab-colori">Colori</TabsTrigger>}
                    {seeMis && <TabsTrigger value="misure" data-testid="tab-misure">Misure</TabsTrigger>}
                    {seeCfz && <TabsTrigger value="confezionamenti" data-testid="tab-confez">Confezionamenti</TabsTrigger>}
                </TabsList>
                {seeFasc && <TabsContent value="fascette" className="mt-4">
                    <AnagrTab resource="fascette" title="Fascette" icon={Ribbon} testPrefix="fasc" fields={[]}
                        extra={[
                            {key:"ean13", label:"EAN-13 (facoltativo)", type:"text", placeholder:"8004567890123",
                             hintFn:(v)=> !v ? <span className="text-slate-500">Se assente, l'etichetta viene stampata senza barcode</span> :
                                              eanChecksumValid(v) ? <span className="text-emerald-400">✓ checksum valido</span> :
                                                                    <span className="text-red-400">✗ checksum errato — al salvataggio ti chiederò conferma</span>},
                            {key:"larg_dichiarata_cm", label:"Larg. dichiarata (cm)", type:"int",
                             hint:"Primo numero della misura stampata sulla fascetta (es. 55 di '55×70')"},
                            {key:"alt_dichiarata_cm", label:"Alt. dichiarata (cm)", type:"int",
                             hint:"Secondo numero della misura stampata (es. 70 di '55×70')"},
                            {key:"buste_per_rotolo", label:"Buste per rotolo (fascetta)", type:"int",
                             hint:"Se valorizzato, sull'etichetta si stampa 'Cf. R × B' invece di 'CF da R ROT.'"},
                        ]} />
                </TabsContent>}
                {seeCart && <TabsContent value="cartoni" className="mt-4">
                    <AnagrTabCartoni />
                </TabsContent>}
                {seeTped && <TabsContent value="pedane" className="mt-4">
                    <AnagrTab resource="tipi-pedana" title="Tipi Pedana" icon={Layers} testPrefix="tped" fields={[]}
                        extra={[
                            {key:"lung_cm", label:"Lung. (cm)", type:"int"},
                            {key:"larg_cm", label:"Larg. (cm)", type:"int"},
                        ]} />
                </TabsContent>}
                {seeImp && <TabsContent value="impianti" className="mt-4">
                    <AnagrTab resource="impianti" title="Impianti / Macchine" icon={Factory} testPrefix="imp" fields={[]}
                        extra={[
                            {key:"larghezza_rullo_cm", label:"Larghezza rullo (cm)", type:"int"},
                            {key:"piste_max", label:"Piste max", type:"int"},
                            {key:"printer_host", label:"Stampante Zebra — IP/host", type:"text", placeholder:"192.168.1.50",
                             hint:"Se vuoto, la stampa etichette scarica lo ZPL invece di inviarlo alla stampante"},
                            {key:"printer_port", label:"Porta stampante", type:"int", placeholder:"9100",
                             hint:"Default 9100 (Zebra raw TCP)"},
                        ]} />
                </TabsContent>}
                {seeCli && <TabsContent value="clienti" className="mt-4">
                    <AnagrTabCodice resource="clienti" title="Clienti" icon={Package} testPrefix="cli" extra={[]} />
                </TabsContent>}
                {seeCol && <TabsContent value="colori" className="mt-4">
                    <AnagrTabCodice resource="colori" title="Colori" icon={Package} testPrefix="col"
                        extra={[
                            {key:"nome_plurale", label:"Nome plurale (usato in etichetta)", type:"text",
                             placeholder:"neri / verdi / rose",
                             hint:"Sull'etichetta: 'SACCHI [PLURALE]'. Suggerito automaticamente dal nome, sempre modificabile."},
                        ]} />
                </TabsContent>}
                {seeMis && <TabsContent value="misure" className="mt-4">
                    <AnagrTabMisure />
                </TabsContent>}
                {seeCfz && <TabsContent value="confezionamenti" className="mt-4">
                    <AnagrTabConfezionamenti />
                </TabsContent>}
            </Tabs>
        </div>
    );
}
